import React, { useEffect, useState, useCallback } from "react";
import moment from 'moment';
import {
  Eye, X, CheckCircle, AlertCircle, Bell, Clock, Users, Building,
  ExternalLink, Upload, AlertTriangle, MapPin, DollarSign, Calendar,
  Heart, MessageCircle, ThumbsUp, UserPlus, Share2, Briefcase, User,
  TrendingUp, Activity
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSocket } from "../../contexts/SocketContext";
import profilePicture from "../../images/profilepic.png";

const typeIcons = {
  job: <Building className="text-blue-600" size={20} />,
  internship: <Users className="text-pink-700" size={20} />,
  event: <Clock className="text-yellow-700" size={20} />,
  post: <Bell className="text-green-700" size={20} />,
  admin: <AlertCircle className="text-red-600" size={20} />,
};

// Activity type icons
const activityIcons = {
  heart: <Heart className="text-red-500" size={14} />,
  'thumbs-up': <ThumbsUp className="text-blue-500" size={14} />,
  smile: <span className="text-yellow-500 text-sm">😊</span>,
  clap: <span className="text-green-500 text-sm">👏</span>,
  comment: <MessageCircle className="text-blue-500" size={14} />,
  post: <Share2 className="text-green-500" size={14} />,
  follow: <UserPlus className="text-purple-500" size={14} />
};

function NotificationCenterPage() {
  const socket = useSocket();
  const profile = useSelector(state => state.profile);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const portalInfo = useSelector((state) => state.portalInfo.data);
  const portalType = portalInfo?.portalType || 'Community';
  // const [jobOpenings] = useState(dummyJobOpenings);

  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(socket?.connected);



  // Fetch recent activities
  const fetchRecentActivities = useCallback(async () => {
    if (!profile._id) return;

    setActivitiesLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recent-activity/user/${profile._id}?limit=8`);
      const result = await response.json();

      if (result.success) {
        setRecentActivities(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching recent activities:", error);
    }
    setActivitiesLoading(false);
  }, [profile._id]);

  // **Fetch notifications from API**
  const fetchNotifications = useCallback(async () => {
    if (!profile._id) return;

    setLoading(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/notification-center/user/${profile._id}`);
      const result = await resp.json();

      if (result.success) {
        setNotifications(result.notifications || []);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
    setLoading(false);
  }, [profile._id]);

  // **Mark notification as read**
  const markAsRead = async (notifId) => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/notification-center/${notifId}/read`, {
        method: "PATCH",
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (resp.ok) {
        setNotifications(prev =>
          prev.map(n => n._id === notifId ? { ...n, read: true } : n)
        );
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // **Remove notification**
  const removeNotif = async (notifId) => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/notification-center/${notifId}`, {
        method: "DELETE"
      });

      if (resp.ok) {
        setNotifications(prev => prev.filter(n => n._id !== notifId));
      }
    } catch (error) {
      console.error("Error removing notification:", error);
    }
  };

  // **Handle notification click**
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification._id);
    }

    if (notification.meta?.url) {
      navigate(notification.meta.url);
    }
  };

  // **Request browser notification permission**
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
    }
  };

  // **Show browser notification**
  const showBrowserNotification = (notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const browserNotif = new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification._id,
        badge: '/favicon.ico',
        requireInteraction: notification.priority === 'critical' || notification.priority === 'high',
        silent: false
      });

      if (!browserNotif.requireInteraction) {
        setTimeout(() => browserNotif.close(), 5000);
      }

      browserNotif.onclick = () => {
        window.focus();
        handleNotificationClick(notification);
        browserNotif.close();
      };
    }
  };

  // **Real-time socket event handlers**
  useEffect(() => {
    if (!profile._id) return;

    console.log("🔌 Setting up notification socket listeners for user:", profile._id);

    const handleConnect = () => {
      console.log("✅ Socket connected for notifications");
      setIsConnected(true);
      socket.emit("join-notification-room", profile._id);
    };

    const handleDisconnect = () => {
      console.log("❌ Socket disconnected");
      setIsConnected(false);
    };

    const handleConnectError = (error) => {
      console.error("🔴 Socket connection error:", error);
      setIsConnected(false);
    };

    const handleNewNotification = (notification) => {
      console.log("📧 New notification received:", notification);

      setNotifications(prev => {
        const exists = prev.find(n => n._id === notification._id);
        if (exists) return prev;

        return [notification, ...prev];
      });

      showBrowserNotification(notification);
    };

    const handleNotificationRead = ({ notificationId }) => {
      console.log("👁️ Notification marked as read:", notificationId);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
    };

    const handleNotificationRemoved = ({ notificationId }) => {
      console.log("🗑️ Notification removed:", notificationId);
      setNotifications(prev =>
        prev.filter(n => n._id !== notificationId)
      );
    };

    socket.on("connect", handleConnect);
    // socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    socket.on("new-notification", handleNewNotification);
    socket.on("notification-read", handleNotificationRead);
    socket.on("notification-removed", handleNotificationRemoved);

    if (socket.connected) {
      handleConnect();
    }

    requestNotificationPermission();

    return () => {
      // Cleanup on unmount
    };
  }, [profile._id]);



  // Update the getNotificationStyle function
  const getNotificationStyle = (notification) => {
    // Special styling for specific notification types
    if (notification.title === "ID Verification Required") {
      return "bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500";
    }
    if (notification.read) {
      return notification.read
        ? 'bg-gray-50 border border-gray-100 opacity-70'
        : 'bg-white border-l-4 border-blue-500 shadow-sm ring-1 ring-blue-100';
    }


    if (notification.title === "ID Verification Approved") {
      return "bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500";
    }

    if (notification.title === "ID Verification Rejected") {
      return "bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500";
    }

    if (notification.title === "New ID Verification Request") {
      return "bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500";
    }

    // Enhanced styling for read/unread differentiation
    return notification.read
      ? 'bg-gray-50 border border-gray-100 opacity-70'
      : 'bg-white border-l-4 border-blue-500 shadow-sm ring-1 ring-blue-100';
  };
  // **Filter notifications**
  const getStickyWarningNotifications = () => {
    return notifications.filter(n =>
      n.title === "ID Verification Required" &&
      (n.userId === profile._id || n.global)
    );
  };

  const getRegularNotifications = () => {
    return notifications.filter(n =>
      !(n.title === "ID Verification Required" && (n.userId === profile._id || n.global))
    );
  };

  const stickyWarnings = getStickyWarningNotifications();
  const regularNotifications = getRegularNotifications();
  const unreadCount = notifications.filter(n => !n.read).length;


  // Handle activity click
  const handleActivityClick = (activity) => {
    if (activity.postId) {
      navigate(`/home/posts/${activity.postId}`);
    } else if (activity.userId) {
      navigate(`/home/profile/${activity.userId}`);
    }
  };
  // **Format time ago**
  const formatTimeAgo = (date) => {
    console.log("formate", date)
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    console.log(`${diffInDays}d ago`)
    return `${diffInDays}d ago`;
  };
  // Get activity display text
  const getActivityText = (activity) => {
    switch (activity.type) {
      case 'like':
        return `${activity.action} "${activity.postTitle}"`;
      case 'comment':
        return `${activity.action} "${activity.postTitle}"`;
      case 'post':
        return activity.action;
      case 'follow':
        return `${activity.action} ${activity.targetUserName}`;
      default:
        return activity.action;
    }
  };
  // **Format salary**
  const formatSalary = (min, max, currency) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    if (min && max) {
      return `${formatter.format(min)} - ${formatter.format(max)}`;
    }
    if (min) {
      return `${formatter.format(min)}+`;
    }
    return 'Salary not specified';
  };



  // Initial fetch on mount
  useEffect(() => {
    fetchNotifications();
    fetchRecentActivities();
  }, [fetchNotifications, fetchRecentActivities]);





  return (
    <div className="min-h-screen bg-gray-50 md:p-4 py-2">
      <div className="mx-auto max-w-7xl">

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8  pb-8">
          {  

            portalType === "Community" ?

              <>
                <div className=" col-span-1 lg:col-span-12   space-y-6 ">
                  <div className="">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      {/* Header */}
                      <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <h2 className="text-2xl font-bold text-[#0A3A4C]">All Notifications</h2>
                            {recentActivities.length > 0 && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {recentActivities.length}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={fetchRecentActivities}
                            className="text-gray-500 hover:text-[#0A3A4C] transition-all duration-200 p-2 rounded-lg hover:bg-white/70 hover:shadow-sm active:scale-95"
                            title="Refresh activities"
                          >
                            <TrendingUp size={18} />
                          </button>
                        </div>
                      </div>

                      {/* Activities List */}
                      <div className=" overflow-y-auto">
                        {activitiesLoading ? (
                          <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-[#0A3A4C] mx-auto"></div>
                            <p className="text-sm text-gray-500 mt-3">Loading activities...</p>
                          </div>
                        ) : recentActivities.length === 0 ? (
                          <div className="p-12 text-center text-gray-400">
                            <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                              <User size={32} className="opacity-50" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-600 mb-2">No recent activities</h3>
                            <p className="text-sm">Follow more users to see their activities here</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-50">
                            {recentActivities.map((activity, index) => (
                              <div
                                key={activity.id}
                                onClick={() => handleActivityClick(activity)}
                                className="flex items-start gap-4 p-3 hover:bg-gray-50/80 transition-all duration-200 cursor-pointer group hover:shadow-sm  border border-gray-700"
                              >
                                {/* Profile Picture with enhanced styling */}
                                <div className="relative flex-shrink-0">
                                  <div className="relative">
                                    <img
                                      src={activity.profilePicture || profilePicture}
                                      alt={activity.userName}
                                      className="w-10 h-10 rounded-full border-2 border-white shadow-sm ring-1 ring-gray-100 group-hover:ring-blue-200 transition-all duration-200"
                                    />
                                    {/* Activity Icon Overlay with better positioning */}
                                    <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-1 shadow-md border border-gray-200 group-hover:border-blue-200 transition-all duration-200">
                                      {activityIcons[activity.icon] || activityIcons['heart']}
                                    </div>
                                  </div>
                                </div>

                                {/* Activity Content with improved spacing */}
                                <div className="flex-1 min-w-0 space-y-2">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#0A3A4C] transition-colors">
                                      {activity.userName}
                                    </span>
                                    <span className="text-xs text-gray-400">•</span>
                                    <p className="text-xs text-gray-400 flex-shrink-0">
                                      {moment(activity.timestamp).fromNow()}
                                    </p>
                                  </div>

                                  <p className="text-sm text-gray-600 mb-2 line-clamp-2 leading-relaxed">
                                    {getActivityText(activity)}
                                  </p>

                                  {activity.comment && (
                                    <div className="bg-gray-50 rounded-lg px-3 py-2 border-l-2 border-gray-200">
                                      <p className="text-xs text-gray-600 italic line-clamp-2">
                                        "{activity.comment}"
                                      </p>
                                    </div>
                                  )}

                                  {/* Bottom section with improved layout */}
                                  <div className="flex items-center justify-between pt-1">
                                    <div className="flex items-center gap-2">
                                      {/* Add activity type badge */}
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                        {activity.type || 'Activity'}
                                      </span>
                                    </div>
                                    {activity.postId && (
                                      <div className="flex items-center gap-1 text-gray-400 group-hover:text-[#0A3A4C] transition-colors">
                                        <span className="text-xs">View</span>
                                        <ExternalLink size={12} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                      
                      </div>
                    </div>
                  </div>
                </div>


              </>
              :
              <>
                {/* LEFT SIDEBAR - Enhanced Recent Activities */}
                <div className="lg:col-span-4 space-y-6 order-2 lg:order-1 ">
                  <div className="sticky top-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      {/* Header */}
                      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-[#0A3A4C] flex items-center gap-2">
                            <Activity size={20} className="text-blue-600" />
                            Recent Activity
                          </h3>
                          <button
                            onClick={fetchRecentActivities}
                            className="text-gray-500 hover:text-[#0A3A4C] transition-colors p-1 rounded-lg hover:bg-white/50"
                            title="Refresh activities"
                          >
                            <TrendingUp size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Activities List */}
                      <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                        {activitiesLoading ? (
                          <div className="p-4 text-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0A3A4C] mx-auto"></div>
                            <p className="text-xs text-gray-500 mt-2">Loading activities...</p>
                          </div>
                        ) : recentActivities.length === 0 ? (
                          <div className="p-4 text-center text-gray-400">
                            <User size={24} className="mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No recent activities</p>
                            <p className="text-xs mt-1">Follow more users to see their activities</p>
                          </div>
                        ) : (
                          <div className="space-y-0">
                            {recentActivities.map((activity, index) => (
                              <div
                                key={activity.id}
                                onClick={() => handleActivityClick(activity)}
                                className={`flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50 last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                                  }`}
                              >
                                {/* Profile Picture */}
                                <div className="relative">
                                  <img
                                    src={activity.profilePicture || profilePicture}
                                    alt={activity.userName}
                                    className="w-8 h-8 rounded-full flex-shrink-0 border-2 border-white shadow-sm"
                                  />
                                  {/* Activity Icon Overlay */}
                                  <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border border-gray-200">
                                    {activityIcons[activity.icon] || activityIcons['heart']}
                                  </div>
                                </div>

                                {/* Activity Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1 mb-1">
                                    <span className="text-sm font-semibold text-gray-900 truncate">
                                      {activity.userName}
                                    </span>
                                  </div>

                                  <p className="text-xs text-gray-600 mb-1 line-clamp-2 leading-tight">
                                    {getActivityText(activity)}
                                  </p>

                                  {activity.comment && (
                                    <p className="text-xs text-gray-500 italic mb-1 line-clamp-1 bg-gray-50 px-2 py-1 rounded text-ellipsis">
                                      "{activity.comment}"
                                    </p>
                                  )}

                                  <div className="flex items-center justify-between mt-2">
                                    <p className="text-xs text-gray-400">
                                      {moment(activity.timestamp).fromNow()}
                                    </p>
                                    {activity.postId && (
                                      <ExternalLink size={10} className="text-gray-400" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* View All Button */}
                        {/* {recentActivities.length > 0 && (
                    <div className="p-3 border-t border-gray-100 bg-gray-50">
                      <button
                        onClick={() => navigate('/home/activities')}
                        className="w-full text-center text-xs text-[#0A3A4C] hover:text-blue-600 font-medium transition-colors"
                      >
                        View All Activities →
                      </button>
                    </div>
                  )} */}
                      </div>
                    </div>
                  </div>
                </div>

                {/* **CENTER - Notifications (Wider)** */}
                <div className="lg:col-span-8 order-1 lg:order-2">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        <h2 className="text-2xl font-bold text-[#0A3A4C]">All Notifications</h2>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={fetchNotifications}
                          className="text-gray-500 hover:text-[#0A3A4C] transition-colors p-2 rounded-lg hover:bg-gray-100"
                          title="Refresh notifications"
                        >
                          <Bell size={20} />
                        </button>

                        {unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                            {unreadCount} unread
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {/* Loading state */}
                      {loading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A3A4C]"></div>
                          <span className="ml-2 text-gray-600">Loading notifications...</span>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Sticky Warning Notifications */}
                          {stickyWarnings.map(notif => (
                            <div key={`sticky-${notif._id}`} className="relative">
                              <div
                                className={`${getNotificationStyle(notif)} rounded-lg p-4 cursor-pointer hover:shadow-md transition-all duration-200`}
                                onClick={() => handleNotificationClick(notif)}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0">
                                    <AlertTriangle className="text-red-600" size={24} />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-bold text-red-800 text-lg">{notif.title}</h4>
                                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
                                        URGENT
                                      </span>
                                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                                        LIVE
                                      </span>
                                    </div>
                                    <div className="text-red-700 text-sm font-medium mb-2">{notif.message}</div>
                                    <div className="text-red-600 text-xs mb-3">
                                      {new Date(notif.createdAt).toLocaleString()}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        className="bg-red-600 text-white px-3 py-1 rounded-md text-xs font-medium hover:bg-red-700 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate('/home/profile/profile-settings');
                                        }}
                                      >
                                        <Upload size={12} className="mr-1 inline" />
                                        Upload ID Now
                                      </button>
                                      <span className="text-xs text-red-600">
                                        Days left: {notif.meta?.daysLeft || 'Unknown'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Regular Notifications */}
                          <div className="space-y-0 border border-gray-200 rounded-lg overflow-hidden">
                            {regularNotifications.length === 0 && stickyWarnings.length === 0 && (
                              <div className="p-8 text-center text-gray-400">
                                <Bell size={36} className="mx-auto mb-2 text-[#0A3A4C]" />
                                <p>No notifications yet.</p>
                                <p className="text-sm mt-1">You'll see new notifications here in real-time.</p>
                              </div>
                            )}

                            {regularNotifications.map((notif, index) => (
                              <div key={notif._id}
                                className={`flex items-start gap-3 p-4 transition-all duration-200 cursor-pointer ${getNotificationStyle(notif)} ${!notif.read ? 'hover:bg-blue-50 hover:shadow-md' : 'hover:bg-gray-100'
                                  } ${index !== 0 ? 'border-t border-gray-200' : ''}`}
                                onClick={() => handleNotificationClick(notif)}
                              >
                                <div className="flex-shrink-0">
                                  <div className={`p-1 rounded-full ${!notif.read ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                    {typeIcons[notif.type] || <Bell size={20} />}
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className={`font-semibold ${!notif.read ? 'text-gray-900' : 'text-gray-600'}`}>
                                      {notif.title}
                                    </h4>
                                    {!notif.read && (
                                      <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                        <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                                          NEW
                                        </span>
                                      </div>
                                    )}
                                    {notif.read && (
                                      <CheckCircle className="text-gray-400" size={14} />
                                    )}
                                    {/* Rest of your existing badges */}
                                    {notif.global && (
                                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                        Global
                                      </span>
                                    )}
                                    {notif.priority === 'high' && (
                                      <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                                        High Priority
                                      </span>
                                    )}
                                    {notif.priority === 'critical' && (
                                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                                        Critical
                                      </span>
                                    )}
                                  </div>
                                  <div className={`text-sm ${!notif.read ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                                    {notif.message}
                                  </div>
                                  <div className={`text-xs mt-1 ${!notif.read ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {new Date(notif.createdAt).toLocaleString()}
                                  </div>

                                  {/* Rest of your existing content */}
                                  {notif.title === "New ID Verification Request" && (
                                    <button
                                      className="bg-blue-600 text-white px-3 py-1 rounded-md text-xs font-medium hover:bg-blue-700 transition-colors mt-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate('/home/admin/user-verification');
                                      }}
                                    >
                                      <ExternalLink size={12} className="mr-1 inline" />
                                      Review Request
                                    </button>
                                  )}

                                  {!notif.read && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        markAsRead(notif._id);
                                      }}
                                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#174873] mt-2 hover:underline hover:text-blue-600 transition-colors"
                                    >
                                      <CheckCircle size={12} /> Mark as read
                                    </button>
                                  )}
                                </div>

                                {!notif.global && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeNotif(notif._id);
                                    }}
                                    className={`transition flex-shrink-0 ${!notif.read
                                      ? 'text-gray-400 hover:text-red-600'
                                      : 'text-gray-300 hover:text-red-500'
                                      }`}
                                    title="Delete notification"
                                  >
                                    <X size={16} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
          }


        </div>
      </div>
    </div>
  );
}

export default NotificationCenterPage;
